import ControlledInput from "Components/Common/ControlledInput";
import { StrategicPlanItem } from "Stores/strategicPlanStore";
import React from "react";
import { useContext } from "react";
import { AccordionContext, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { CustomToggle } from "./CustomToggle";
import { PlusIcon } from "Components/Icons/PlusIcon";
import { ArchiveIcon } from "Components/Icons/ArchiveIcon";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import ActiveDragIcon from "Components/Icons/ActiveDrag";
import {DraggableProvidedDragHandleProps} from "react-beautiful-dnd";

const MenuAction = ({ onAddItem, onAddSubItem, disable }: {
    onAddItem?: () => void,
    onAddSubItem?: () => void,
    disable?: boolean
}) => {
    return (
        <Dropdown>
            <Dropdown.Toggle id="dropdown-basic">
                <PlusIcon className={disable ? "drop-icon-disabled" : "drop-icon"} />
            </Dropdown.Toggle>
            {!disable &&
                <Dropdown.Menu>
                    <Dropdown.Item onClick={onAddItem}>Add item</Dropdown.Item>
                    {onAddSubItem && <Dropdown.Item onClick={onAddSubItem}>Add sub-item</Dropdown.Item>}
                </Dropdown.Menu>
            }
        </Dropdown>
    );
};

type ItemContainerProps = {
    item?: StrategicPlanItem,
    level: number,
    onOpenArchive?: () => void,
    eventKey: string,
    onOpenDelete?: () => void,
    onAddItem?: () => void,
    onAddSubItem?: () => void,
    onAddChild?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | ((index: number) => void);
    disable?: boolean;
    enableMenu?: boolean;
    onChange?: () => void;
    controlledInput?: boolean
    inputName?: string
    required?: boolean
    hideArchive?: boolean
    defaultValue?: string,
    toggleButtonRef: React.MutableRefObject<Record<string, HTMLButtonElement | null>>
    dragHandleProps?: DraggableProvidedDragHandleProps | null | undefined
    isEdit: boolean,
}

export const ItemContainer = (
    {
        item,
        level,
        eventKey,
        onOpenArchive,
        onOpenDelete,
        onAddItem,
        onAddSubItem,
        disable,
        enableMenu,
        onChange,
        controlledInput,
        inputName,
        required,
        hideArchive,
        defaultValue,
        toggleButtonRef,
        dragHandleProps,
        isEdit,
}: ItemContainerProps) => {
    const { activeEventKey } = useContext(AccordionContext);
    const levels = { 0: "first", 1: "second", 2: "third", 3: "fourth" };


    const renderControlledInput = () => {
        if (isEdit && controlledInput) {
            return (
                <ControlledInput
                    required={required}
                    className="input-strategic-plans"
                    name={inputName || ""}
                    onInputChange={onChange}
                    itemRef=""
                    defaultValue={defaultValue}
                    placeholder="Plan item - Example"
                />
            );
        } else {
            return <div className={item?.status === "archived" ? "archived-item" : ""}>{item?.name ?? ""}</div>;
        }
    };

    return (
        <div className={`${levels[level]} item-container`}>
            <div className="item-not-status">
                <div className="item-elements-arrow">
                    {item && <button
                        type="button"
                        className={`order_toggle_${eventKey}`}
                        {...dragHandleProps}
                        style={{background: "transparent", border: "none", cursor: "grab"}}
                    ><ActiveDragIcon/>
                    </button>}
                    {level !== 3 && (
                        <CustomToggle
                            level={level}
                            toggleButtonRef={toggleButtonRef}
                            eventKey={eventKey}
                            disabled={item?.status === "archived" ? true : enableMenu ? false : !item?.children?.length}
                        />
                    )}
                    <div className="item-elements-name text-click">
                        <MenuAction
                            onAddItem={onAddItem && onAddItem}
                            onAddSubItem={onAddSubItem && onAddSubItem}
                            disable={item?.status === "archived" ? true : disable}
                        />
                        {renderControlledInput()}
                    </div>
                </div>
                <div className="item-elements-actions">
                    {!hideArchive && (
                        item?.status === "archived" ?
                            <div className={disable ? "drop-icon-disabled" : "unarchive-action"} onClick={disable ? () => { } : onOpenArchive}>Unarchive</div> :
                            <OverlayTrigger overlay={
                                <Tooltip className="tooltip-st-status">
                                    Archive the item
                                </Tooltip>
                            } placement="top">
                                <ArchiveIcon className={disable ? "drop-icon-disabled" : "drop-icon"} onClick={disable ? () => { } : onOpenArchive} />
                            </OverlayTrigger>
                    )}
                    <OverlayTrigger overlay={
                        <Tooltip className="tooltip-st-status">
                            Delete plan item
                        </Tooltip>
                    } placement="top">
                        <DeleteIcon className={disable ? "drop-icon-disabled" : "drop-icon"} onClick={disable ? () => { } : onOpenDelete} />
                    </OverlayTrigger>
                    <div className="vertical-divider" />
                </div>
            </div>
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`tooltip-${item?.id}`} className="tooltip-st-status">
                    Open
                </Tooltip>
            }>
                <div className="action-plans-count open" data-testid="open-item-value">
                    {activeEventKey ? item?.action_plan_statuses?.open_time ?? 0 :
                        item?.total_children_action_plan_statuses?.open_time ?? 0}
                </div>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`tooltip-${item?.id}`} className="tooltip-st-status">
                    Met
                </Tooltip>
            }>
                <div className="action-plans-count met" data-testid="meet-item-value">
                    {activeEventKey ? item?.action_plan_statuses?.closed_goal_met ?? 0 :
                        item?.total_children_action_plan_statuses?.closed_goal_met ?? 0}
                </div>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`tooltip-${item?.id}`} className="tooltip-st-status">
                    Unmet
                </Tooltip>
            }>
                <div className="action-plans-count unmet" data-testid="unmet-item-value">
                    {activeEventKey ? item?.action_plan_statuses?.closed_goal_not_met ?? 0 :
                        item?.total_children_action_plan_statuses?.closed_goal_not_met ?? 0}
                </div>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`tooltip-${item?.id}`} className="tooltip-st-status">
                    No Date Assigned
                </Tooltip>
            }>
                <div className="action-plans-count observation" data-testid="observation-item-value">
                    {activeEventKey ? item?.action_plan_statuses?.no_date_assigned ?? 0 :
                        item?.total_children_action_plan_statuses?.no_date_assigned ?? 0}
                </div>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={
                <Tooltip id={`tooltip-${item?.id}`} className="tooltip-st-status">
                    Overdue
                </Tooltip>
            }>
                <div className="action-plans-count overdue" data-testid="overdue-item-value">
                    {activeEventKey ? item?.action_plan_statuses?.open_late ?? 0 :
                        item?.total_children_action_plan_statuses?.open_late ?? 0}
                </div>
            </OverlayTrigger>
        </div>
    );
};
